import React, { useMemo } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';

import { DataBlocks } from 'components';
import { Status} from '@beef/legacy-ui-kit';
import { SUBSCRIPTION_STATUSES } from 'utils/partnershipTariffStatus';
import { Text, Link } from '@beef/ui-kit';
import styles from './styles.pcss';
import { SubscriptionStatus } from './components/SubscriptionStatus';

const cx = classNames.bind(styles);

const PartnershipTariff = ({
  data,
  subscriptionInfo,
  subscriptionDisabled,
  hasSubscriptionPending,
}) => {
  const {
    title,
    description,
    serviceIcons,
    statusNotAvailable,
    buttonText,
    iconPlus,
    connected,
    suspended,
    bonusPlusIcon,
    subscriptionStatusText,
    subscriptionDisabledInfo,
  } = data;
  const {
    status,
    account,
    bonus,
    activationRedirectUrl,
    isSucceeded,
    statusSynchronizationText,
    isSubscriptionDisabled,
  } = subscriptionInfo;

  const calculatedDescription = useMemo(() => {
    if (status === SUBSCRIPTION_STATUSES.available || status === SUBSCRIPTION_STATUSES.notAvailable || !isSucceeded) {
      return description;
    }
    if (status === SUBSCRIPTION_STATUSES.connected) {
      return connected.description;
    }
    if (status === SUBSCRIPTION_STATUSES.suspended) {
      return suspended.description;
    }
  }, [status]);

  const isPlusIconShown = useMemo(() => (status === SUBSCRIPTION_STATUSES.connected
    || status === SUBSCRIPTION_STATUSES.suspended) && !account && !bonus, [status, account, bonus]);

  const isBonusShown = useMemo(() => (status === SUBSCRIPTION_STATUSES.connected
    || status === SUBSCRIPTION_STATUSES.suspended) && account && (bonus || bonus === 0), [status, account, bonus]);

  return (
    <DataBlocks.Block
      title={title}
    >
      {
        (status === SUBSCRIPTION_STATUSES.available
          || status === SUBSCRIPTION_STATUSES.notAvailable
          || !isSucceeded) && (
          <div className={cx('services')}>
            {serviceIcons.map((icon, i) => (
              <div key={i} className={cx('service')}>
                <img src={icon} alt="ya-service" />
              </div>
            ))}
          </div>
        )
      }
      {
        isBonusShown && (
          <div className={cx('account-info')}>
            <div className={cx('account-info-bonus', {
              'account-info-bonus--inactive': status === SUBSCRIPTION_STATUSES.suspended,
            })}
            >
              <img src={bonusPlusIcon} alt="ya-bonus" className={cx('account-info-bonus-icon')} />
              {bonus}
            </div>
            <span className={cx('account-info-mail')}>
              {account}
            </span>
          </div>
        )
      }
      <div className={cx('description')}>
        {
          isPlusIconShown && (
            <img src={iconPlus} alt="ya-plus" className={cx('description-icon')} />
          )
        }
        <div
          dangerouslySetInnerHTML={{ __html: calculatedDescription }}
          className={cx('description-text', {
            'description-text--plain': status === SUBSCRIPTION_STATUSES.connected
              || status === SUBSCRIPTION_STATUSES.suspended,
            'description-text--active': account && bonus,
            'description-text--alert': status === SUBSCRIPTION_STATUSES.suspended,

          })}
        />
      </div>
      {
        (status === SUBSCRIPTION_STATUSES.notAvailable || !isSucceeded) && (
          <Status
            className={[cx('warning'), 'attention', 'compact', 'transformer']}
          >
            {statusNotAvailable}
          </Status>
        )
      }
      {
        (!subscriptionDisabled && status === SUBSCRIPTION_STATUSES.available && activationRedirectUrl) && (
          <>
            {hasSubscriptionPending
              ? (
                <SubscriptionStatus
                  statusText={subscriptionStatusText}
                />
              )
              : (<a className={cx('subscription')} href={activationRedirectUrl}>{buttonText}</a>)}
          </>
        )
      }
      {
        (status === SUBSCRIPTION_STATUSES.notAvailable || !isSucceeded
          || (status === SUBSCRIPTION_STATUSES.available && subscriptionDisabled)) && (
          <button type="button" disabled className={cx('subscription--fail')}>
            {buttonText}
          </button>
        )
      }
      {statusSynchronizationText && !isSubscriptionDisabled && (
        <SubscriptionStatus
          statusText={statusSynchronizationText}
          size="38"
        />
      )}
      {isSubscriptionDisabled && subscriptionDisabledInfo && (
        <div className={cx('subscription-disabled')}>
          <Text color="scarlet">
            {subscriptionDisabledInfo.text}
            &nbsp;
            <Link href={subscriptionDisabledInfo.redirectLink}>
              {subscriptionDisabledInfo.redirectText}
            </Link>
          </Text>
        </div>
      )}
    </DataBlocks.Block>
  );
};

PartnershipTariff.propTypes = {
  data: PropTypes.object,
  subscriptionInfo: PropTypes.objectOf(PropTypes.shape({
    status: PropTypes.string,
    account: PropTypes.string,
    bonus: PropTypes.number,
    activationRedirectUrl: PropTypes.string,
    isSucceeded: PropTypes.bool,
  })),
  subscriptionDisabled: PropTypes.bool,
  hasSubscriptionPending: PropTypes.bool,
};

export default PartnershipTariff;
